/*
(c) Copyright European Space Agency, 2023

Project name: THEMIS: Tracking the Health of the Environment and Missions in Space

Description:  Module for Debris Index Per PMD Reliability chart
Author: Deimos Space UK

Version: 1.0
*/
import React, { useState, useEffect } from "react";
import { Card, CardBody, Button, Row, Alert } from "reactstrap";
import { useParams } from "react-router-dom";
import Spinner from 'react-bootstrap/Spinner';
import { getPetition } from "../../../../api/api";
import Chart from 'react-apexcharts';
import { ESA_COLORS } from "../../../../core/models/colors";
import { saveFile, rawDataIntoExcel } from "../../../../api/excel-api";

export default function IndexTimeEvaluation({ spacecraftId, constellation, spacecraftIdOrigin }) {
  const chartContainerStyle = {
    width: '100%',
  };

  const { id } = useParams();
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [barChartData, setGraphData] = useState([{}])

  useEffect(() => {

    if (spacecraftId && constellation) {
      setLoading(true)
      getPetition(`/api/debris_index/debris_index_per_pmd_reliability_for_spacecraft?spacecraft_id=${spacecraftId}`)
        .then((data) => {

          setBarCharData([data])
          setLoading(false)
          setError(false)
        })
        .catch((error) => {
          setLoading(false)
          setError(true)
        })
    }
    else if (spacecraftIdOrigin.length > 0) {
      setLoading(true)
      getPetition(`/api/debris_index/debris_index_per_pmd_reliability_for_spacecraft?spacecraft_id=${spacecraftIdOrigin[0]}`)
        .then((data) => {
          setBarCharData([data])
          setLoading(false)
          setError(false)
        })
        .catch((error) => {
          setLoading(false)
          setError(true)
        })
    }
  }, [constellation, spacecraftId, spacecraftIdOrigin]);

  const setBarCharData = (data) => {
    const versions = []
    if (data?.length > 0) {
      const pmdValues = []
      const noDisposal = []
      const targetedDisposal = []
      const directDisposal = []
      const reorbitDisposal = []
      for (let i = 0; i < data.length; i++) {
        versions.push('Version ' + (i + 1))
        if (data[i]?.pmd_reliability) {
          pmdValues.push(parseFloat(data[i]?.pmd_reliability).toExponential(2))
        } else {
          pmdValues.push(0)
        }
        if (data[i]?.no_disposal) {
          noDisposal.push(parseFloat(data[i]?.no_disposal).toExponential(2))
        } else {
          noDisposal.push(0)
        }
        if (data[i]?.targeted_disposal) {
          targetedDisposal.push(parseFloat(data[i]?.targeted_disposal.toExponential(2)))
        } else {
          targetedDisposal.push(0)
        }
        if (data[i]?.direct_disposal) {
          directDisposal.push(parseFloat(data[i]?.direct_disposal.toExponential(2)))
        } else {
          directDisposal.push(0)
        }
        if (data[i]?.reorbit_disposal) {
          reorbitDisposal.push(parseFloat(data[i]?.reorbit_disposal.toExponential(2)))
        } else {
          reorbitDisposal.push(0)
        }
      }

      let BarData = [
        {
          name: 'No disposal',
          data: noDisposal,
        },

        {
          name: 'Target-time deorbit',
          data: targetedDisposal,
        },
        {
          name: 'Direct Disposal',
          data: directDisposal,
        },
        {
          name: 'Target-orbit deorbit',
          data: reorbitDisposal,
        }
      ]

      const series = []
      data.map((item, index) => {
        const seriesData = BarData.map((item) => {
          return (item.data[index] || 0)
        }
        )
        series.push({
          name: pmdValues[index],
          data: seriesData

        })
      })
      setGraphData(series)
    }

  }

  const apexBarChartOpts = {
    chart: {
      height: 380,
      type: 'bar',
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,

      },
    },
    dataLabels: {
      enabled: false,
      offsetX: -6,
      formatter: function (val) {
        return val.toExponential(3)
      },
      style: {
        fontSize: '12px',
        colors: ['#fff'],
      },
    },
    colors: ESA_COLORS,
    stroke: {
      show: true,
      width: 0.2,
      colors: ['#fff'],
    },

    xaxis: {
      categories: ['No disposal', 'Target-time deorbit', 'Direct Disposal', 'Target-orbit deorbit'],
      labels: {
        show: true,
        maxHeight: 100,
        rotateAlways: false,
        rotate: 0,
        style: {
          colors: [],
          fontSize: '8px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          cssClass: 'apexcharts-xaxis-label',
        },
        offsetX: 0,


      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return val.toExponential(3)
        },
      
      },
      forceNiceScale: false,
      tickAmount: 4,
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      showForNullSeries: true,
      showForZeroSeries: true,
    },
    states: {
      hover: {
        filter: 'none',
      },
    },
    grid: {
      borderColor: '#f1f3fa',
    },

  };

  const fetchDataToExcel = () => {
    const excelRows = [];
    barChartData.map((item, index) => {
      const row = {
        'Version': index + 1,
        'PMD Reliability': item.name,
        'No disposal': item.data[0],
        'Target-time deorbit': item.data[1],
        'Direct Disposal': item.data[2],
        'Target-orbit deorbit': item.data[3],
      }
      excelRows.push(row)
    }
    )
    const fileName = 'Debris Index Per PMD Reliability'
    saveFile(rawDataIntoExcel(excelRows), fileName)

  }

  if (loading) {
    return (
      <Card className="widget-flat h-100">
        <CardBody>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
            <Spinner animation="border" role="status"></Spinner>
          </div>
        </CardBody>
      </Card>
    )
  }
  if (error) {
    return (
      <Card className="widget-flat h-100">
        <CardBody>
          <Alert color="danger" role="alert">
            <h4 className="alert-heading">Error</h4>
            <p>There was an error processing this request. Please try again later.</p>
          </Alert>
        </CardBody>
      </Card>
    )
  }

  return (
    <Card className="widget-flat h-100 ">
      <CardBody>
        <Row >
          <h4 style={{ color: '#003247' }} className="header-title float-left col " >DEBRIS INDEX PER PMD RELIABILITY</h4>
          <Button style={{ background: '#003247', fontSize: '11px' }} onClick={() => fetchDataToExcel()} className="float-right"><i className="uil  uil-down-arrow "></i></Button>
        </Row>
        <div className="bar-container " style={chartContainerStyle}>
          <Chart
            options={apexBarChartOpts}
            series={barChartData}
            type="bar"
            width={'100%'}
            className="apex-charts" />
        </div>

      </CardBody>
    </Card>
  );
}
